import React from 'react';

import {
  Card,
  CardContent,
  CardHeader,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import {useQuery} from 'react-query';

import API, {Types, GroupMemberPaginationProps} from '@wheeebo/polaris-api';
import {CircleLoader, GroupMemberPanel, LoadError} from '@wheeebo/polaris-ui';

const INITIAL_ITEM_COUNT = 10;

interface RowProps {
  groupMember: Types.GroupMember;
  onSelect: () => void;
}

function Row({groupMember, onSelect}: RowProps) {
  return (
    <TableRow hover sx={{cursor: 'pointer'}} onClick={onSelect}>
      <TableCell>
        <GroupMemberPanel groupMember={groupMember} />
      </TableCell>
    </TableRow>
  );
}

interface Props {
  groupId: Types.UUID;
  onMemberSelected: (memberId: Types.UUID) => void;
}

const GroupMemberListCard = ({groupId, onMemberSelected}: Props): JSX.Element => {
  const [page, setPage] = React.useState(0);
  const options: GroupMemberPaginationProps = {
    limit: INITIAL_ITEM_COUNT,
    offset: page * INITIAL_ITEM_COUNT,
    preload: ['member'],
  };
  const {data, isError, isLoading} = useQuery(API.GroupMembers.listKey(groupId, options), () =>
    API.GroupMembers.list(groupId, options),
  );
  return (
    <Card variant="outlined">
      <CardHeader title="Members" subheader="The members of this group." />
      <CardContent>
        {isLoading ? <CircleLoader /> : null}
        {isError ? <LoadError /> : null}
        {data && data.total > 0 ? (
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableBody>
                {data?.items?.map((e: Types.GroupMember) => (
                  <Row key={e.id} groupMember={e} onSelect={() => onMemberSelected(e.member.id)} />
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[INITIAL_ITEM_COUNT]}
                    count={data?.total || 0}
                    rowsPerPage={INITIAL_ITEM_COUNT}
                    page={page}
                    SelectProps={{
                      inputProps: {'aria-label': 'rows per page'},
                    }}
                    onPageChange={(_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) =>
                      setPage(newPage)
                    }
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body2">No record found.</Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default React.memo(GroupMemberListCard);
